import { useDebounceFn } from 'ahooks'
import { Avatar, Divider, Input, List } from 'antd'
import cls from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { GroupAvatar } from '@/components'
import {
  PostType,
  RequestedGroupFragment,
  UserInfoFragment,
  useGetGroupListQuery,
  useGetGroupSelectorListQuery,
} from '@/generated/graphql'

import s from './post-editor.module.less'
import { PostEditorSetState } from './post-editor.state'

export const PostEditorSelectorList: React.FC<{
  viewer: UserInfoFragment
  postType?: PostType
  setGroupId: React.Dispatch<React.SetStateAction<number | undefined>>
  setStep?: React.Dispatch<React.SetStateAction<number>>
  onSelect?: () => void
  setState?: PostEditorSetState
}> = ({ viewer, postType, setStep, onSelect, setGroupId, setState }) => {
  const { t } = useTranslation()

  const [search, setSearchStr] = React.useState('')
  const { run: setSearch } = useDebounceFn(setSearchStr, { wait: 300 })

  const { data, error } = useGetGroupSelectorListQuery()
  const fetchCount = search ? 6 : 4
  const { data: following, error: followingError } = useGetGroupListQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      userId: viewer.userId,
      ...{ withViewer: true, withFollowing: true },
      pagination: {
        last: fetchCount,
      },
      filter: {
        ...(!!search && { search }),
      },
    },
  })

  if (error || followingError) {
    return null
  }

  const header = (
    <Input.Search
      className={s.search}
      onChange={(e) => setSearch(e.currentTarget.value)}
      onSearch={(s) => setSearch(s)}
      placeholder={t('search.searchGroups')}
    />
  )

  const activeGroups = data?.viewer.activeGroups
  const recommendGroups = data?.recommend.selectorsRecommendedGroups
  const followingGroups = following?.viewer!.followingGroups!.edges.map(({ node }) => node)

  const onGroupClick = (g: RequestedGroupFragment) => {
    setGroupId(g.groupId)
    setState && setState((state) => ({ ...state!, isAnonymous: g.canPostAnonymously ? state!.isAnonymous : false }))
    postType === PostType.Event ? setStep!(1) : onSelect!()
  }

  const activeList = activeGroups?.map((g) => (
    <List.Item className={s.listItem} key={g.groupId} onClick={() => onGroupClick(g)}>
      <List.Item.Meta
        avatar={<GroupAvatar groupCatId={g.category?.groupCategoryId} />}
        title={
          <span>
            {g.name}
            <span className={cls(s.groupTag, s.mostlyUsed)}>{t('groups.selector.mostlyUsed')}</span>
          </span>
        }
      />
    </List.Item>
  ))

  const recommendList = recommendGroups?.map((g) => (
    <List.Item className={s.listItem} key={g.groupId} onClick={() => onGroupClick(g)}>
      <List.Item.Meta
        avatar={<GroupAvatar groupCatId={g.category?.groupCategoryId} />}
        title={
          <span>
            {g.name}
            <span className={cls(s.groupTag, s.recommend)}>{t('groups.selector.recommend')}</span>
          </span>
        }
      />
    </List.Item>
  ))

  const groupsList = followingGroups?.map((g) => (
    <List.Item className={s.listItem} key={g.groupId} onClick={() => onGroupClick(g)}>
      <List.Item.Meta avatar={<GroupAvatar groupCatId={g.category?.groupCategoryId} />} title={g.name} />
    </List.Item>
  ))

  return (
    <div className={s.postOptions}>
      <List header={header} split={false}>
        {!search && activeList}
        {!search && recommendList}
        {groupsList}
      </List>
      {postType !== PostType.Event && (
        <>
          <Divider style={{ margin: 0 }} />
          <List.Item
            className={s.listItem}
            onClick={() => {
              setGroupId(undefined)
              onSelect!()
            }}
          >
            <List.Item.Meta
              avatar={<Avatar size='small' src={viewer.avatarUrl} />}
              title={t('post.editor.myTimeline')}
            />
          </List.Item>
        </>
      )}
    </div>
  )
}
